/**
 * Core Application Module
 * Handles app initialization, global state, and module coordination
 * TypeScript implementation with proper type safety
 * Sprint 31: Updated for environment-aware communication
 */

// Sprint 36: Import environment and proxy services
import { environment } from '../config/environment';
import { ollamaWarmup } from '../api/proxy-services';
// Sprint 59: LLM switcher
import { llmSwitcher } from '../ui/components/llm-switcher';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Application global state
 */
interface GlobalState {
    currentPage: string;
    user: UserInfo | null;
    theme: 'light' | 'dark';
}

/**
 * User information
 */
interface UserInfo {
    id: string;
    username: string;
    email: string;
    role: string;
}

/**
 * Module registry type
 */
interface ModuleRegistry {
    config?: any;
    canisterClient?: CanisterClient;
    pageManager?: any;
    toastNotifications?: any;
    discoveryLogger?: any;
    networkDiscovery?: any;
    dcmsDiscovery?: any;
    deviceManager?: any;
    vectorSearch?: any;
    credentialManager?: any;
    aiAssistant?: any;
    iiAgent?: any;
    iiAgentUI?: any;
    authManager?: any;
    [key: string]: any;
}

/**
 * Custom authentication event detail
 */
interface AuthEventDetail {
    username: string;
    userId: string;
    token: string;
}

// ============================================================================
// AppCore Class
// ============================================================================

/**
 * Main application core class
 * Manages initialization, module loading, and global state
 */
export class AppCore {
    private initialized: boolean;
    private modules: ModuleRegistry;
    private globalState: GlobalState;

    constructor() {
        this.initialized = false;
        this.modules = {};
        this.globalState = {
            currentPage: 'dashboard',
            user: null,
            theme: 'light'
        };
    }

    /**
     * Initialize the application
     */
    async init(): Promise<void> {
        if (this.initialized) return;

        try {
            console.log('🚀 ASD ICP v3 - Initializing modular application...');

            // Load core dependencies
            await this.loadCoreModules();

            // Initialize modules in order
            await this.initializeModules();

            // Setup global event listeners
            this.setupGlobalListeners();

            // Initialize routing
            this.initializeRouting();

            // Initialize authentication UI
            await this.initializeAuth();

            this.initialized = true;
            console.log('✅ Application initialized successfully');

            // Show initial page (will handle auth requirements)
            this.showInitialPage();

        } catch (error) {
            console.error('❌ Application initialization failed:', error);
            this.showErrorState(error instanceof Error ? error : new Error(String(error)));
        }
    }

    /**
     * Load core modules (via script tags in HTML)
     */
    private async loadCoreModules(): Promise<void> {
        console.log('📦 Core modules loaded via HTML script tags');
    }

    /**
     * Initialize all application modules in dependency order
     */
    private async initializeModules(): Promise<void> {
        console.log('🔧 Initializing application modules...');

        // Initialize ConfigManager
        if ((window as any).ConfigManager) {
            this.modules.config = new (window as any).ConfigManager();
            console.log('✅ ConfigManager initialized');
        }

        // Use existing global CanisterClient instance
        if ((window as any).canisterClient) {
            this.modules.canisterClient = (window as any).canisterClient;
            console.log('✅ CanisterClient registered from global instance');
        } else if ((window as any).CanisterClient) {
            this.modules.canisterClient = new (window as any).CanisterClient();
            console.log('✅ CanisterClient initialized from class');
        } else {
            console.error('❌ CanisterClient not available - neither instance nor class found');
        }

        // Use existing PageManager instance (don't create duplicate)
        if ((window as any).pageManagement) {
            this.modules.pageManager = (window as any).pageManagement;
            console.log('✅ PageManager (existing instance) registered');
        } else if ((window as any).PageManager) {
            this.modules.pageManager = new (window as any).PageManager();
            console.log('✅ PageManager initialized (new instance)');
        }

        // Use existing ToastNotifications instance
        if ((window as any).toastNotifications) {
            this.modules.toastNotifications = (window as any).toastNotifications;
            console.log('✅ ToastNotifications (existing instance) registered');
        } else if ((window as any).ToastNotifications) {
            this.modules.toastNotifications = new (window as any).ToastNotifications();
            console.log('✅ ToastNotifications initialized (new instance)');
        }

        // Use existing DiscoveryLogger instance
        if ((window as any).discoveryLogger) {
            this.modules.discoveryLogger = (window as any).discoveryLogger;
            console.log('✅ DiscoveryLogger (existing instance) registered');
        } else if ((window as any).DiscoveryLogger) {
            this.modules.discoveryLogger = new (window as any).DiscoveryLogger();
            console.log('✅ DiscoveryLogger initialized (new instance)');
        }

        // Initialize NetworkDiscovery (no existing instance in main.ts)
        if ((window as any).NetworkDiscovery) {
            this.modules.networkDiscovery = new (window as any).NetworkDiscovery();
            console.log('✅ NetworkDiscovery initialized');
        }

        // Use existing DCMSDiscovery instance
        if ((window as any).dcmsDiscovery) {
            this.modules.dcmsDiscovery = (window as any).dcmsDiscovery;
            console.log('✅ DCMSDiscovery (existing instance) registered');
        } else if ((window as any).DCMSDiscovery) {
            this.modules.dcmsDiscovery = new (window as any).DCMSDiscovery();
            console.log('🔧 DCMSDiscovery dependencies check:', {
                discoveryLogger: !!this.modules.discoveryLogger,
                canisterClient: !!this.modules.canisterClient
            });
            if (this.modules.discoveryLogger && this.modules.canisterClient) {
                this.modules.dcmsDiscovery.setDependencies(
                    this.modules.discoveryLogger,
                    this.modules.canisterClient
                );
                console.log('✅ DCMSDiscovery dependencies injected successfully');
            } else {
                console.warn('⚠️ DCMSDiscovery dependencies not available yet');
            }
            console.log('✅ DCMSDiscovery initialized (new instance)');
        }

        // Initialize DeviceManager (no existing instance in main.ts)
        if ((window as any).DeviceManager) {
            this.modules.deviceManager = new (window as any).DeviceManager();
            console.log('✅ DeviceManager initialized');
        }

        // Use existing VectorSearch instance
        if ((window as any).vectorSearch) {
            this.modules.vectorSearch = (window as any).vectorSearch;
            // Set dependencies after canister client is ready
            if (this.modules.canisterClient) {
                this.modules.vectorSearch.setDependencies(
                    this.modules.canisterClient,
                    this.modules.discoveryLogger
                );
            }
            console.log('✅ VectorSearch initialized');
        }

        // Initialize CredentialManager
        if ((window as any).CredentialManager) {
            this.modules.credentialManager = new (window as any).CredentialManager();
            console.log('✅ CredentialManager initialized');
        }

        // Initialize AI Assistant
        if ((window as any).aiAssistant) {
            this.modules.aiAssistant = (window as any).aiAssistant;
            if (this.modules.canisterClient) {
                this.modules.aiAssistant.initialize(this.modules.canisterClient)
                    .then(() => {
                        console.log('✅ AI Assistant initialized successfully');
                    })
                    .catch((error: Error) => {
                        console.error('❌ AI Assistant initialization failed:', error);
                    });
            } else {
                console.warn('⚠️ CanisterClient not available for AI Assistant');
            }
        } else {
            console.warn('⚠️ AI Assistant module not loaded');
        }

        // Initialize II-Agent
        if ((window as any).iiAgent) {
            this.modules.iiAgent = (window as any).iiAgent;
            if (this.modules.canisterClient) {
                this.modules.iiAgent.initialize(this.modules.canisterClient)
                    .then(() => {
                        console.log('✅ II-Agent initialized successfully');
                        // Also initialize the UI
                        if ((window as any).iiAgentUI) {
                            this.modules.iiAgentUI = (window as any).iiAgentUI;
                            this.modules.iiAgentUI.initialize();
                            console.log('✅ II-Agent UI initialized successfully');
                        }
                    })
                    .catch((error: Error) => {
                        console.error('❌ II-Agent initialization failed:', error);
                    });
            } else {
                console.warn('⚠️ CanisterClient not available for II-Agent');
            }
        } else {
            console.warn('⚠️ II-Agent module not loaded');
        }

        // Initialize AuthManager
        if ((window as any).authManager) {
            this.modules.authManager = (window as any).authManager;
            if (this.modules.canisterClient) {
                await this.modules.authManager.initialize(this.modules.canisterClient);
                console.log('✅ AuthManager initialized');
            } else {
                console.error('❌ AuthManager initialization failed - CanisterClient not available');
            }
        } else {
            console.error('❌ AuthManager not available');
        }

        // Post-initialization: Set dependencies for modules that need them
        this.setupDependencies();
    }

    /**
     * Setup post-initialization dependencies
     */
    private setupDependencies(): void {
        console.log('🔧 Setting up post-initialization dependencies...');
        console.log('🔧 Available modules:', Object.keys(this.modules));
        console.log('🔧 Module status:', {
            dcmsDiscovery: !!this.modules.dcmsDiscovery,
            discoveryLogger: !!this.modules.discoveryLogger,
            canisterClient: !!this.modules.canisterClient,
            vectorSearch: !!this.modules.vectorSearch
        });

        // Ensure DCMSDiscovery has its dependencies
        if (this.modules.dcmsDiscovery && this.modules.discoveryLogger && this.modules.canisterClient) {
            this.modules.dcmsDiscovery.setDependencies(
                this.modules.discoveryLogger,
                this.modules.canisterClient
            );
            console.log('✅ DCMSDiscovery dependencies set in post-initialization');
        } else {
            console.error('❌ DCMSDiscovery post-init failed - missing:', {
                dcmsDiscovery: !this.modules.dcmsDiscovery,
                discoveryLogger: !this.modules.discoveryLogger,
                canisterClient: !this.modules.canisterClient
            });
        }

        // Ensure VectorSearch has its dependencies
        if (this.modules.vectorSearch && this.modules.canisterClient) {
            this.modules.vectorSearch.setDependencies(
                this.modules.canisterClient,
                this.modules.discoveryLogger
            );
            console.log('✅ VectorSearch dependencies set in post-initialization');
        } else {
            console.error('❌ VectorSearch post-init failed - missing:', {
                vectorSearch: !this.modules.vectorSearch,
                canisterClient: !this.modules.canisterClient
            });
        }
    }

    /**
     * Setup global event listeners
     */
    private setupGlobalListeners(): void {
        // Global error handler
        window.addEventListener('error', (event) => {
            console.error('💥 Global error:', event.error);
            this.modules.toastNotifications?.show('Application Error', 'error');
        });

        // Unhandled promise rejection handler
        window.addEventListener('unhandledrejection', (event) => {
            console.error('💥 Unhandled promise rejection:', event.reason);
            this.modules.toastNotifications?.show('Promise Rejection', 'error');
        });

        // Page visibility changes
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                console.log('👀 Application became visible');
            }
        });
    }

    /**
     * Initialize routing system
     */
    private initializeRouting(): void {
        // Handle initial page load
        const urlParams = new URLSearchParams(window.location.search);
        const requestedPage = urlParams.get('page');

        if (requestedPage) {
            this.globalState.currentPage = requestedPage;
        }

        // Setup popstate handler for back/forward navigation
        window.addEventListener('popstate', (event) => {
            if (event.state && (event.state as any).page) {
                this.showPage((event.state as any).page);
            }
        });
    }

    /**
     * Show initial page after initialization
     */
    private showInitialPage(): void {
        // Check for pending page load from before modules were ready
        if ((window as any).pendingPageLoad) {
            console.log('📄 Found pending page load:', (window as any).pendingPageLoad);
            this.showPage((window as any).pendingPageLoad);
            delete (window as any).pendingPageLoad;
        } else {
            this.showPage(this.globalState.currentPage);
        }
    }

    /**
     * Get a module by name
     */
    getModule(moduleName: string): any {
        return this.modules[moduleName];
    }

    /**
     * Get current application state
     */
    getState(): GlobalState {
        return { ...this.globalState };
    }

    /**
     * Update application state
     */
    setState(newState: Partial<GlobalState>): void {
        this.globalState = { ...this.globalState, ...newState };
        this.notifyStateChange();
    }

    /**
     * Notify listeners of state changes
     */
    private notifyStateChange(): void {
        window.dispatchEvent(new CustomEvent('appStateChange', {
            detail: this.globalState
        }));
    }

    /**
     * Initialize authentication system
     * Sprint 44: Adds first-run detection, setup wizard, change password, user menu
     */
    private async initializeAuth(): Promise<void> {
        if (!this.modules.authManager) {
            console.error('❌ AuthManager not available for initialization');
            return;
        }

        try {
            // Initialize login form handlers
            this.modules.authManager.initializeLoginForm();
            this.modules.authManager.initializeLogout();

            // Initialize setup wizard (first-run flow)
            this.modules.authManager.initializeSetupWizard();

            // Initialize change password modal
            this.modules.authManager.initializeChangePasswordModal();

            // Initialize user menu dropdown (header)
            this.modules.authManager.initializeUserMenu();

            // Set up authentication event listeners
            this.setupAuthEventListeners();

            // Update UI based on current authentication state
            this.updateAuthUI();

            // Check if user needs to authenticate for the current page
            await this.checkAuthRequirements();

            console.log('✅ Authentication system initialized');
        } catch (error) {
            console.error('❌ Authentication initialization failed:', error);
        }
    }

    /**
     * Setup authentication event listeners
     */
    private setupAuthEventListeners(): void {
        // Listen for authentication events
        document.addEventListener('auth:login', ((event: CustomEvent<AuthEventDetail>) => {
            console.log('User logged in:', event.detail);
            this.globalState.user = event.detail as any;
            this.hideStartupScreen();
            this.showMainApplication();
            this.updateAuthUI();
            this.notifyStateChange();
            // Warm up Ollama if Self-Hosted LLM is enabled (Sprint 14)
            this.warmupOllamaIfEnabled();
        }) as EventListener);

        document.addEventListener('auth:logout', () => {
            console.log('User logged out');
            this.globalState.user = null;
            this.showStartupScreen();
            this.hideMainApplication();
            this.updateAuthUI();
            this.notifyStateChange();
        });

        document.addEventListener('auth:sessionValidated', ((event: CustomEvent<AuthEventDetail>) => {
            console.log('Session validated:', event.detail);
            this.globalState.user = event.detail as any;
            this.hideStartupScreen();
            this.showMainApplication();
            this.updateAuthUI();
            this.notifyStateChange();
            // Warm up Ollama if Self-Hosted LLM is enabled (Sprint 14)
            this.warmupOllamaIfEnabled();
        }) as EventListener);
    }

    /**
     * Warm up Ollama model if Self-Hosted LLM is enabled
     * Sprint 14: Ollama Model Warm-up on App Init
     *
     * Only triggers warmup when:
     * - User has "Self-Hosted LLM" selected in settings (aiBackend === 'llm')
     * - Runs in background (non-blocking) so it doesn't delay app startup
     */
    private warmupOllamaIfEnabled(): void {
        try {
            // Check if Self-Hosted LLM is enabled in settings
            const SettingsService = (window as any).SettingsService;
            if (!SettingsService) {
                console.log('⏭️ Ollama warmup skipped: SettingsService not available');
                return;
            }

            const settings = new SettingsService();
            const aiBackend = settings.getAiBackend();

            if (aiBackend !== 'llm') {
                console.log(`⏭️ Ollama warmup skipped: AI backend is '${aiBackend}', not 'llm'`);
                return;
            }

            const activeModel = settings.getAiModel();
            console.log(`Warming up Ollama model '${activeModel}' (Self-Hosted LLM enabled)...`);

            // Make non-blocking warmup call via proxy-services
            ollamaWarmup(activeModel).then(loadTimeMs => {
                if (loadTimeMs !== null) {
                    console.log(`Ollama model warmed up in ${loadTimeMs}ms`);
                }
            });

        } catch (error) {
            console.warn('⚠️ Ollama warmup check failed:', error);
        }
    }

    /**
     * Update UI based on authentication state
     */
    private updateAuthUI(): void {
        if (this.modules.authManager) {
            this.modules.authManager.updateUI();

            // Initialize LLM switcher when user is authenticated (Sprint 59)
            if (this.modules.authManager.isAuthenticated()) {
                llmSwitcher.initialize();
            }

            // Update user initials in header
            const userInitialElements = document.querySelectorAll<HTMLElement>('[data-initial="true"]');
            const user = this.modules.authManager.getCurrentUser();
            if (user && userInitialElements.length > 0) {
                const initial = user.username.charAt(0).toUpperCase();
                userInitialElements.forEach(el => {
                    el.textContent = initial;
                });
            }

            // Initialize feather icons for any new elements
            if ((window as any).feather && typeof (window as any).feather.replace === 'function') {
                try {
                    (window as any).feather.replace();
                } catch (error) {
                    console.warn('⚠️ Feather icons replace failed:', error);
                }
            } else {
                console.warn('⚠️ Feather icons not loaded yet, deferring...');
                // Try again after a short delay
                setTimeout(() => {
                    if ((window as any).feather && typeof (window as any).feather.replace === 'function') {
                        try {
                            (window as any).feather.replace();
                            console.log('✅ Feather icons loaded successfully on retry');
                        } catch (error) {
                            console.warn('⚠️ Feather icons replace failed on retry:', error);
                        }
                    }
                }, 100);
            }
        }
    }

    /**
     * Check authentication requirements for current page.
     * Sprint 44: First-run detection — show setup wizard if no users exist.
     */
    private async checkAuthRequirements(): Promise<void> {
        const isAuthenticated = this.modules.authManager?.isAuthenticated();

        if (!isAuthenticated) {
            this.showStartupScreen();
            this.hideMainApplication();

            // Check if this is a first-run (no users) — show setup wizard
            try {
                const isFirstRun = await this.modules.authManager?.checkFirstRun();
                if (isFirstRun) {
                    console.log('🆕 First run detected — showing setup wizard');
                    this.modules.authManager?.showSetupWizard();
                    return;
                }
            } catch (error) {
                console.warn('⚠️ First-run check failed, falling back to login:', error);
            }

            this.modules.authManager?.showLoginModal();
        } else {
            this.hideStartupScreen();
            this.showMainApplication();
        }
    }

    /**
     * Show startup screen
     */
    private showStartupScreen(): void {
        const startupScreen = document.getElementById('startup-screen');
        if (startupScreen) {
            startupScreen.style.display = 'flex';
            console.log('✅ Startup screen shown');
        }
    }

    /**
     * Hide startup screen
     */
    private hideStartupScreen(): void {
        const startupScreen = document.getElementById('startup-screen');
        if (startupScreen) {
            startupScreen.style.display = 'none';
            console.log('✅ Startup screen hidden');
        }
    }

    /**
     * Show main application
     */
    private showMainApplication(): void {
        const mainApp = document.getElementById('main-app');
        if (mainApp) {
            mainApp.style.display = 'flex';
            console.log('✅ Main application shown');
        }
    }

    /**
     * Hide main application
     */
    private hideMainApplication(): void {
        const mainApp = document.getElementById('main-app');
        if (mainApp) {
            mainApp.style.display = 'none';
            console.log('✅ Main application hidden');
        }
    }

    /**
     * Show a specific page (authentication-aware)
     */
    showPage(pageName: string): void {
        // Check authentication before showing page
        if (!this.modules.authManager?.isAuthenticated()) {
            // Don't show login if setup wizard is already visible (first-run)
            const setupWizard = document.getElementById('setupWizardModal');
            if (setupWizard && setupWizard.style.display !== 'none') {
                return;
            }
            this.modules.authManager?.showLoginModal();
            return;
        }

        if (this.modules.pageManager) {
            this.modules.pageManager.showPage(pageName);
            this.globalState.currentPage = pageName;

            // Update URL without page reload
            const url = new URL(window.location.href);
            url.searchParams.set('page', pageName);
            window.history.pushState({ page: pageName }, '', url.toString());
        } else {
            console.warn('⚠️ PageManager not available, deferring page load');
            (window as any).pendingPageLoad = pageName;
        }
    }

    /**
     * Show error state when initialization fails
     */
    private showErrorState(error: Error): void {
        document.body.innerHTML = `
            <div class="min-h-screen bg-red-50 flex items-center justify-center">
                <div class="bg-white p-8 rounded-lg shadow-lg max-w-md">
                    <div class="text-red-600 text-xl font-bold mb-4">
                        Application Error
                    </div>
                    <div class="text-gray-700 mb-4">
                        The application failed to initialize properly.
                    </div>
                    <div class="text-sm text-gray-500 bg-gray-100 p-3 rounded">
                        ${error.message}
                    </div>
                    <button onclick="window.location.reload()"
                            class="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">
                        Reload Application
                    </button>
                </div>
            </div>
        `;
    }
}

// ============================================================================
// Browser Integration
// ============================================================================

declare global {
    interface Window {
        AppCore: typeof AppCore;
        appCore: AppCore;
        pendingPageLoad?: string;
    }
}

// Global app instance
window.appCore = new AppCore();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.appCore.init());
} else {
    window.appCore.init();
}

// Expose globally for other modules
window.AppCore = AppCore;

// Export for module system
